import React, { useMemo, useState } from 'react';
import { Badge, Input, Spinner } from 'reactstrap';
import { useMutation } from '@apollo/client/react';
import { useAuth } from '../../contexts/AuthContext';
import {
  UPDATE_ORDER_PAYMENT_STATUS,
  UpdateOrderPaymentStatusData,
  UpdateOrderPaymentStatusVariables,
} from '../../graphql/mutations/orders/updateOrderPaymentStatus';
import {
  getPaymentBadgeColor,
  NGK_PAYMENT_STATUS_META_KEY,
  normalizePaymentStatus,
  PAYMENT_STATUS_OPTIONS,
  paymentStatusToIsPaid,
  resolvePaymentStatus,
  type PaymentStatusValue,
} from '../../utils/bookingOrderHelpers';
import type { OrderNode } from '../../graphql/queries/orders';

interface PaymentStatusSelectProps {
  order: Pick<OrderNode, 'databaseId' | 'status' | 'metaData'>;
  compact?: boolean;
}

const PaymentStatusSelect: React.FC<PaymentStatusSelectProps> = ({ order, compact = true }) => {
  const { hasRole, isSuperAdmin, isCustomerPortalUser } = useAuth();
  const canChange =
    !isCustomerPortalUser() &&
    (isSuperAdmin() || hasRole('admin_business'));
  const current = resolvePaymentStatus(order);
  const [pending, setPending] = useState<PaymentStatusValue | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [updatePaymentStatus, { loading }] = useMutation<
    UpdateOrderPaymentStatusData,
    UpdateOrderPaymentStatusVariables
  >(UPDATE_ORDER_PAYMENT_STATUS, {
    refetchQueries: ['GetOrders', 'GetCustomerOrders', 'GetOrder'],
    awaitRefetchQueries: true,
  });

  const options = useMemo(() => {
    const list: Array<{ value: PaymentStatusValue; label: string }> = [
      ...PAYMENT_STATUS_OPTIONS,
    ];
    if (current && !list.some((option) => option.value === current)) {
      list.unshift({ value: current, label: current });
    }
    return list;
  }, [current]);

  if (!canChange) {
    return <Badge color={getPaymentBadgeColor(current)}>{current}</Badge>;
  }

  const display = pending || current;

  const onChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = normalizePaymentStatus(event.target.value);
    if (!next || next === current) return;

    setError(null);
    setPending(next);
    try {
      await updatePaymentStatus({
        variables: {
          orderId: order.databaseId,
          metaData: [{ key: NGK_PAYMENT_STATUS_META_KEY, value: next }],
          isPaid: paymentStatusToIsPaid(next),
        },
      });
      setPending(null);
    } catch (err: any) {
      setPending(null);
      const gqlMessage =
        err?.graphQLErrors?.[0]?.message ||
        err?.networkError?.result?.errors?.[0]?.message;
      setError(gqlMessage || err?.message || 'Unable to update payment status.');
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center gap-1">
        <Input
          type="select"
          bsSize={compact ? 'sm' : undefined}
          value={display}
          disabled={loading}
          onChange={onChange}
          className="fw-medium"
          style={{ minWidth: compact ? 120 : 150 }}
          title="Change payment status"
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Input>
        {loading ? <Spinner size="sm" color="primary" /> : null}
      </div>
      {error ? <div className="text-danger small mt-1">{error}</div> : null}
    </div>
  );
};

export default PaymentStatusSelect;
